import { useEffect, useState } from 'react'

import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { DataItem, provideComponent, useDataScope } from 'scrivito'

import {
  clearDashboardActiveTab,
  getStoredDashboardActiveTab,
  persistDashboardActiveTab,
} from './dashboardTabStorage'
import { DashboardChartComponent } from './DXPDashboardChartComponent'
import { DXPDashboardWidget } from './DXPDashboardWidget'
import { NavTabItem, NavTabs } from '@/Atomic/Templates/NavTabs/NavTabs'
import { DataErrorEditorNote } from '@/Components/DataErrorEditorNote'
import { EditorNote } from '@/Components/EditorNote'
import { DATA_SCOPE_ERROR, ErrorVariant } from '@/Components/error/constants'
import { ErrorPage } from '@/Components/error/Errors'
import {
  PisaSalesChartStyle,
  PisaSalesChartType,
} from '@/Data/Dashboard/DashboardDataClass'
import { ensureString, ensureStringButNot } from '@/utils/ensureString'
import { getDataItems } from '@/utils/getDataItems'
import { isDevEnv } from '@/utils/isRuntimeEnv'

import './dashboard.scss'

/** we support max. 100 charts, this should be way enough :-) */
const MAXIMAL_CHARTS = 100
/** we render 3 chart items per row */
const ITEMS_PER_ROW = 3
/** the default group "Dashboard" */
const DEFAULT_GROUP = 'Dashboard'
/** translation prefix */
const TRANSLATION_PREFIX = 'components.dashboard.'

/** dashboard item properties */
interface DIProps {
  /** row index */
  index: number
  /** row's data items */
  items: DataItem[]
}

/**
 * a dashboard row component, hosts a row of dashboard chart items
 * @param param0 row properties
 * @returns the elements to render a row of charts
 */
const DashboardRowComponent: React.FC<DIProps> = ({
  index,
  items,
}: DIProps) => {
  return (
    <div className="dxp-dashboard-row">
      {items.map((item, iix) => {
        try {
          const id = item.id()
          const element = ensureString(item.get('element'))
          const title = ensureString(item.get('title'))
          const group = ensureString(item.get('group'))
          const type = ensureString(item.get('type')) as PisaSalesChartType
          const style = ensureString(item.get('style')) as PisaSalesChartStyle
          const cix = index * MAXIMAL_CHARTS + iix

          // Always use V2 component
          return (
            <DashboardChartComponent
              key={`chart${cix}`}
              id={id}
              element={element}
              group={group}
              title={title}
              type={type}
              style={style}
            />
          )
        } catch (error) {
          const chartId = item.id()
          console.error(`Error rendering chart with ID: ${chartId}`, error)
          return (
            <div
              key={`chart-error-${index}-${iix}`}
              className="dxp-dashboard-item card"
            >
              <div className="alert alert-danger m-3">
                <strong>Chart Error</strong>
                <p>Failed to render chart: {chartId}</p>
                <small>
                  {error instanceof Error ? error.message : String(error)}
                </small>
              </div>
            </div>
          )
        }
      })}
    </div>
  )
}

/**
 * Dashboard page properties
 */
interface PageProps extends DIProps {
  currentIndex: number
}

/**
 * a dashboard page component, hosts a dashbaord page
 * @param param0 page properties
 * @returns the elements to render a dashbaord page
 */
const DashboardPageComponent: React.FC<PageProps> = ({
  index,
  items,
  currentIndex,
}: PageProps) => {
  const rows: DataItem[][] = []
  const count = items.length
  for (let i = 0; i < count; ++i) {
    const item = items[i] as DataItem
    let row: DataItem[] = []
    if (i % ITEMS_PER_ROW === 0) {
      rows.push(row)
    } else {
      row = rows[rows.length - 1] as DataItem[]
    }
    row.push(item as DataItem)
  }
  const classNames: string[] = ['dxp-dashboard-page']
  if (index == currentIndex) {
    classNames.push('dxp-dashbaord-page-active')
  } else {
    classNames.push('d-md-none')
    classNames.push('dxp-dashboard-page-hidden')
  }
  return (
    <div className={classNames.join(' ')}>
      {rows.map((row, ri) => {
        const rix = index * MAXIMAL_CHARTS + ri
        return (
          <DashboardRowComponent key={`row${rix}`} index={rix} items={row} />
        )
      })}
    </div>
  )
}

/**
 * provides a possibly localized type name
 * @param item data item
 * @param t translation
 * @returns the effective type name
 */
function getLocalizedType(
  item: DataItem,
  t: TFunction<'translation', unknown>,
): string {
  const raw_type = ensureStringButNot(item.get('type'), 'user', DEFAULT_GROUP)
  if (DEFAULT_GROUP === raw_type) {
    // default group
    return raw_type
  }
  if (raw_type.charAt(0).toLowerCase() !== raw_type.charAt(0)) {
    // starts with uppercase character --> use it as is
    return raw_type
  }
  const translated = t(`${TRANSLATION_PREFIX}${raw_type}`)
  if (translated.startsWith(TRANSLATION_PREFIX)) {
    // not translated
    return raw_type
  }
  return translated
}

/**
 * provide the Dashboard component
 */
provideComponent(DXPDashboardWidget, () => {
  const { t } = useTranslation()
  const [activeTabTitle, setActiveTabTitle] = useState<string>(() => {
    return getStoredDashboardActiveTab() || ''
  })

  let renderError: React.ReactElement | null = null
  let runtimeError = false
  let groupEntries: Array<[string, DataItem[]]> = []

  try {
    const { dataItems, error: dataError } = getDataItems(
      useDataScope()?.transform({ limit: MAXIMAL_CHARTS }),
    )
    if (dataError === DATA_SCOPE_ERROR) {
      renderError = <EditorNote>No Data Scope is defined</EditorNote>
    } else if (dataError) {
      renderError = <DataErrorEditorNote error={dataError} />
    } else if (dataItems.length > 0) {
      // sort items by group
      const groupMap: Map<string, DataItem[]> = new Map()
      for (let i = 0; i < dataItems.length; ++i) {
        const item = dataItems[i] as DataItem
        if (isDevEnv()) {
          console.log(
            `>>> Item #${i}: GID="${item.id()}" type="${item.get('type')}" title="${item.get('title')}" element="${item.get('element')}" group="${item.get('group')}"`,
          )
        }
        const type = getLocalizedType(item, t)
        const group = ensureString(item.get('group'), type)
        if (!groupMap.has(group)) {
          // group not known yet --> add it
          groupMap.set(group, [])
        }
        groupMap.get(group)?.push(item)
      }
      groupEntries = Array.from(groupMap.entries())
      if (isDevEnv()) {
        console.log('>>> Dashboard groups:', ...groupMap)
      }
    }
  } catch (e) {
    console.warn('Failure fetching dashboard data!', e)
    runtimeError = true
  }

  const groupTitles = groupEntries.map(([title]) => title)
  const tabTitle = groupTitles.includes(activeTabTitle)
    ? activeTabTitle
    : groupTitles[0] || ''
  const tabIndex = Math.max(groupTitles.indexOf(tabTitle), 0)

  useEffect(() => {
    if (groupEntries.length === 0) return

    if (activeTabTitle !== tabTitle) {
      setActiveTabTitle(tabTitle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabTitle])

  useEffect(() => {
    if (groupEntries.length === 0) return

    if (groupTitles.length <= 1) {
      clearDashboardActiveTab()
      return
    }

    if (tabTitle) {
      persistDashboardActiveTab(tabTitle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabTitle, groupTitles.length])

  if (renderError) {
    return renderError
  }
  if (runtimeError) {
    return <ErrorPage variant={ErrorVariant.InternalServerError} />
  }
  if (groupEntries.length === 0) {
    return null
  }

  const dashboardTabs: NavTabItem[] = groupTitles.map((title) => ({
    id: title,
    title,
  }))

  return (
    <div className="dxp-chart-container">
      <div className="dxp-dashbaord-tab-host">
        <NavTabs
          tabs={dashboardTabs}
          variant="large"
          activeTab={tabTitle}
          onTabChange={setActiveTabTitle}
          renderPanels={false}
        />
      </div>
      <div className="dxp-dashbaord-page-host">
        {groupEntries.map(([, items], index) => {
          return (
            <DashboardPageComponent
              key={`page${index}`}
              index={index}
              items={items}
              currentIndex={tabIndex}
            />
          )
        })}
      </div>
    </div>
  )
})
